import { useState } from "react";
import { Smartphone, Share, Plus, MoreVertical, Apple, X } from "lucide-react";
import { useLang } from "@/i18n";
import WeeklyGiftsCta from "@/components/WeeklyGiftsCta";

function detectPlatform() {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
  if (/Android/.test(ua)) return "android";
  return "other";
}

const PLATFORMS = [
  {
    id: "ios",
    label: "iPhone / iPad (Safari)",
    Badge: Apple,
    steps: [
      { key: "installIos1", Icon: Share },
      { key: "installIos2", Icon: Plus },
      { key: "installIos3", Icon: Smartphone },
    ],
  },
  {
    id: "android",
    label: "Android (Chrome)",
    Badge: Smartphone,
    steps: [
      { key: "installAndroid1", Icon: MoreVertical },
      { key: "installAndroid2", Icon: Plus },
      { key: "installAndroid3", Icon: Smartphone },
    ],
  },
];

export default function InstallPrompt({ onClose, onSignUp }) {
  const { t } = useLang();
  const platform = detectPlatform();
  const [tab, setTab] = useState("install");
  const [showOther, setShowOther] = useState(platform === "other");
  const primary = PLATFORMS.find((p) => p.id === platform) || PLATFORMS[0];
  const others = PLATFORMS.filter((p) => p.id !== primary.id);
  const shown = showOther ? [primary, ...others] : [primary];

  const tabCls = (id) =>
    `flex-1 pixel-btn px-3 py-2.5 text-[10px] ${tab === id ? "bg-[#0067C6] text-white" : "bg-[#222] text-white/65"}`;

  return (
    <div
      className="fixed inset-0 z-[10050] overflow-y-auto overscroll-contain flex items-start justify-center bg-black/85 p-4 safe-x safe-t safe-b"
      data-testid="install-prompt"
    >
      <div className="pixel-card bg-[#141414] w-full max-w-md p-6 md:p-7 pop-in relative overflow-y-auto max-h-full my-auto">
        <button
          data-testid="install-close-btn"
          onClick={onClose}
          className="pixel-btn bg-black/55 text-white p-2 absolute top-3 right-3"
          aria-label="Close"
        >
          <X size={16} strokeWidth={2.5} />
        </button>

        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#0067C6] flex items-center justify-center shrink-0 shadow-lg">
            <Smartphone size={24} strokeWidth={2.5} className="text-white" />
          </div>
          <h2 className="font-pixel text-sm md:text-base text-[#FFEB3B] pixel-stroke leading-snug pr-8" data-testid="install-title">
            {t("installTitle")}
          </h2>
        </div>

        <div className="mt-3 rounded-xl bg-[#0067C6]/20 ring-1 ring-[#0067C6]/50 px-3 py-2.5" data-testid="install-benefit">
          <p className="font-vt text-base text-white leading-tight">{t("installBenefit")}</p>
        </div>

        {/* Tabs keep the popup short — gifts/sign-in vs install directions */}
        <div className="flex gap-2 mt-4" role="tablist">
          <button data-testid="install-tab-gifts" onClick={() => setTab("gifts")} className={tabCls("gifts")}>
            {t("tabGifts")}
          </button>
          <button data-testid="install-tab-install" onClick={() => setTab("install")} className={tabCls("install")}>
            {t("tabInstall")}
          </button>
        </div>

        {tab === "gifts" && <WeeklyGiftsCta prefix="install" onSignUp={onSignUp} className="mt-4" />}

        {tab === "install" && (
          <div className="mt-4">
            <p className="font-vt text-base text-white/80 leading-tight mb-3">{t("installSubtitle")}</p>
            <div className="flex flex-col gap-5">
              {shown.map((pf) => (
                <div key={pf.id} data-testid={`install-platform-${pf.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <pf.Badge size={16} strokeWidth={2.5} className="text-[#0067C6]" />
                    <span className="font-pixel text-[10px] text-white/90 uppercase tracking-wide">{pf.label}</span>
                    {pf.id === platform && (
                      <span className="font-pixel text-[8px] text-black bg-[#FFEB3B] px-1.5 py-0.5 rounded">{t("yourDevice")}</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 pl-1">
                    {pf.steps.map((s, i) => (
                      <div key={s.key} className="flex items-start gap-3" data-testid={`install-${pf.id}-step-${i + 1}`}>
                        <div className="shrink-0 w-7 h-7 rounded-full bg-[#FF5722] text-white font-pixel text-[10px] flex items-center justify-center mt-0.5">
                          {i + 1}
                        </div>
                        <div className="flex items-center gap-2 flex-1">
                          <s.Icon size={16} strokeWidth={2.5} className="text-[#FFEB3B] shrink-0" />
                          <p className="font-vt text-base text-white leading-tight text-left">{t(s.key)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {!showOther && others.length > 0 && (
                <button data-testid="install-show-other" onClick={() => setShowOther(true)}
                  className="font-vt text-base text-[#4FC3F7] underline self-start">
                  {t("otherDevice")}
                </button>
              )}
            </div>
          </div>
        )}

        <button
          data-testid="install-gotit-btn"
          onClick={onClose}
          className="pixel-btn bg-[#4CAF50] text-white w-full py-4 text-sm mt-6"
        >
          {t("installGotIt")}
        </button>
      </div>
    </div>
  );
}
